import { type Day } from '@choco-admin/types';
import { useCallback } from 'react';

import { useNotification } from '../../../contexts/NotificationContext';
import { useUpdateDeliverySettingMutation } from '../../../generated';

interface UseUpdateDeliverySettingConfig {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  /**
   * Suppress this hook's own success/error toasts and only invoke the
   * `onSuccess`/`onError` callbacks. Used by the create flow so a single upper
   * layer owns the user-facing notifications; defaults to `false` so the
   * Edit-chat delivery modal keeps its toasts.
   */
  silent?: boolean;
}

const END_OF_DAY = '23:59:59';

interface DaySettings {
  type: 'strict' | 'flexible' | 'noCutoff';
  leadTime?: number;
  cutoffTime?: string;
}

export const useUpdateDeliverySetting = ({
  onSuccess,
  onError,
  silent = false,
}: UseUpdateDeliverySettingConfig = {}) => {
  const { addNotification } = useNotification();
  const [updateDeliverySetting, { loading }] =
    useUpdateDeliverySettingMutation();

  const updateSettings = useCallback(
    async ({
      chatId,
      deliveryDays,
      cutoffType,
      cutoffTimes,
      cutoffDays,
    }: {
      chatId: string;
      deliveryDays: Record<Day, boolean>;
      cutoffType: string;
      cutoffTimes: Record<Day, string>;
      cutoffDays: Record<Day, string>;
    }) => {
      try {
        // Only include days that are enabled
        const enabledDays = Object.entries(deliveryDays).reduce<
          Record<string, DaySettings>
        >((acc, [day, isEnabled]) => {
          if (isEnabled) {
            const shortDay = day.toLowerCase().slice(0, 3);
            if (cutoffType === 'no_cutoff') {
              acc[shortDay] = {
                type: 'noCutoff',
              };
            } else {
              acc[shortDay] = {
                type: cutoffType === 'strict' ? 'strict' : 'flexible',
                // The backend rejects flexible days without a cutoffTime too.
                cutoffTime: cutoffTimes[day as Day] || END_OF_DAY,
                leadTime: parseInt(cutoffDays[day as Day], 10),
              };
            }
          }
          return acc;
        }, {});

        const result = await updateDeliverySetting({
          variables: {
            id: chatId,
            cutoffTimes: enabledDays,
          },
        });

        // Mutations resolve on GraphQL errors (errorPolicy: 'all').
        const [firstError] = result.errors ?? [];
        if (firstError) {
          throw new Error(firstError.message);
        }

        if (!silent) {
          addNotification({
            title: 'Success',
            message: 'Delivery settings updated successfully',
            type: 'success',
          });
        }

        onSuccess?.();
      } catch (error) {
        if (!silent) {
          addNotification({
            title: 'Error',
            message: 'Failed to update delivery settings',
            type: 'error',
          });
        }
        onError?.(error instanceof Error ? error : new Error(String(error)));
      }
    },
    [updateDeliverySetting, addNotification, onSuccess, onError, silent]
  );

  return {
    updateSettings,
    isUpdating: loading,
  };
};
